import { Stack, Typography } from '@mui/material';
import PaiDotLoader from 'features/ui/base-components/pai-dot-loader';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import { InboxCampaign, InboxContact } from './funnel-inbox.types';

const UnreadMark: React.FC<{ count: number }> = ({ count }) => {
  const { classes } = useStyles();
  return <div className={classes.unreadMark}>{count}</div>;
};

const getInitials = (name: string | undefined | null): string => {
  if (!name || typeof name !== 'string') {
    return '?';
  }
  const trimmedName = name.trim();
  if (!trimmedName) {
    return '?';
  }
  const parts = trimmedName.split(/\s+/);
  if (parts.length === 1) {
    return parts[0].charAt(0).toUpperCase();
  }
  const firstInitial = parts[0].charAt(0).toUpperCase();
  const lastInitial = parts[parts.length - 1].charAt(0).toUpperCase();
  return firstInitial + lastInitial;
};

interface CampaignItemProps {
  selectedCampaign: string | false;
  campaign: InboxCampaign;
  sendMessageLoading: boolean;

  handleSelectedCampaignChange(value: string): () => void;
}

const CampaignItem: React.FC<CampaignItemProps> = ({ selectedCampaign, campaign, sendMessageLoading, handleSelectedCampaignChange }) => {
  const { classes, cx } = useStyles();

  const selected = selectedCampaign === campaign._id;
  const hasUnreadMessages = campaign.unreadMessagesCount > 0;

  return (
    <Stack
      direction={'row'}
      alignItems={'center'}
      gap={1}
      className={cx(classes.campaignItem, selected ? 'selected' : undefined, sendMessageLoading ? 'disabled' : undefined)}
      onClick={!sendMessageLoading ? handleSelectedCampaignChange(campaign._id) : undefined}
    >
      <Typography className={cx(classes.campaignName, selected ? 'selected' : undefined)}>{campaign.name}</Typography>
      {hasUnreadMessages ? (
        <span className={classes.unreadMarkContainer}>
          <UnreadMark count={campaign.unreadMessagesCount} />
        </span>
      ) : null}
    </Stack>
  );
};

interface ContactItemProps {
  active: boolean;
  contact: InboxContact;
  sendMessageLoading: boolean;

  handleActiveContactChange(): void;
}

const ContactItem: React.FC<ContactItemProps> = ({ active, contact, sendMessageLoading, handleActiveContactChange }) => {
  const { classes, cx } = useStyles();

  return (
    <Stack
      direction={'row'}
      gap={1.5}
      className={cx(classes.contactItem, active ? 'selected' : undefined, sendMessageLoading ? 'disabled' : undefined)}
      onClick={!sendMessageLoading ? handleActiveContactChange : undefined}
    >
      <div className={classes.contactAvatar}>{getInitials(contact.name)}</div>
      <Stack className={classes.contactTexts} gap={'2px'}>
        <Typography className={cx(classes.contactName, contact.hasUnreadMessages ? 'unread' : undefined)}>{contact.name}</Typography>
        <Typography className={classes.contactOccupation}>{contact.occupation || '-'}</Typography>
      </Stack>
      {contact.hasUnreadMessages ? <span className={classes.unreadDot} /> : null}
    </Stack>
  );
};

interface Props {
  campaigns: InboxCampaign[];
  campaignsLoading: boolean;
  totalCampaigns: number;
  totalUnreadMessages: number;

  selectedCampaign: string | false;

  contactsLoading: boolean;
  contacts: InboxContact[];

  selectedContact: string | false;
  sendMessageLoading: boolean;

  handleSelectedCampaignChange(value: string): () => void;

  handleSelectedContactChange(value: string): () => void;
}

const CampaignList: React.FC<Props> = ({
  campaigns,
  campaignsLoading,
  totalCampaigns,
  totalUnreadMessages,
  selectedCampaign,
  contactsLoading,
  contacts,
  selectedContact,
  sendMessageLoading,
  handleSelectedCampaignChange,
  handleSelectedContactChange,
}) => {
  const { classes, cx } = useStyles();
  const { t } = useTranslation();

  const unreadContactsCount = contacts.filter((contact) => contact.hasUnreadMessages).length;

  return (
    <>
      <Stack className={cx(classes.panel, classes.campaignPanel)}>
        <Stack className={classes.panelHeader} direction={'row'} alignItems={'center'} gap={1}>
          <Typography className={classes.panelTitle}>{t('inboxCampaignsColumn')}</Typography>
          <Stack className={classes.panelMetaContainer} direction={'row'} alignItems={'center'} gap={1}>
            {totalUnreadMessages > 0 ? <UnreadMark count={totalUnreadMessages} /> : null}
            <Typography className={classes.panelMeta}>{totalCampaigns}</Typography>
          </Stack>
        </Stack>
        <div className={classes.panelList}>
          {campaignsLoading ? (
            <div className={classes.panelLoader}>
              <PaiDotLoader />
            </div>
          ) : (
            campaigns.map((campaign) => (
              <CampaignItem
                key={campaign._id}
                selectedCampaign={selectedCampaign}
                campaign={campaign}
                sendMessageLoading={sendMessageLoading}
                handleSelectedCampaignChange={handleSelectedCampaignChange}
              />
            ))
          )}
        </div>
      </Stack>

      <Stack className={cx(classes.panel, classes.contactPanel)}>
        <Stack className={classes.panelHeader} direction={'row'} alignItems={'center'} gap={1}>
          <Typography className={classes.panelTitle}>{t('inboxContactsColumn')}</Typography>
          {selectedCampaign && unreadContactsCount > 0 ? (
            <Typography className={cx(classes.panelMetaContainer, classes.panelMeta)}>
              {t('inboxUnreadCount', { count: unreadContactsCount })}
            </Typography>
          ) : null}
        </Stack>
        <div className={classes.panelList}>
          {!selectedCampaign ? (
            <Typography className={classes.panelHint}>{t('inboxSelectCampaignHint')}</Typography>
          ) : contactsLoading ? (
            <div className={classes.panelLoader}>
              <PaiDotLoader />
            </div>
          ) : (
            contacts.map((contact) => (
              <ContactItem
                key={contact.profileId}
                active={selectedContact === contact.profileId}
                contact={contact}
                sendMessageLoading={sendMessageLoading}
                handleActiveContactChange={handleSelectedContactChange(contact.profileId as string)}
              />
            ))
          )}
        </div>
      </Stack>
    </>
  );
};

const useStyles = makeStyles()((theme) => ({
  panel: {
    height: '100%',
    flexShrink: 0,
    background: '#FFF',
    borderRadius: '12px',
    overflow: 'hidden',
  },
  campaignPanel: {
    width: '290px',
  },
  contactPanel: {
    width: '320px',
  },
  panelHeader: {
    padding: '16px',
    borderBottom: `1px solid ${theme.palette.greys.lighter}`,
  },
  panelTitle: {
    fontSize: theme.typography.pxToRem(13),
    fontWeight: 500,
    letterSpacing: '.04em',
    textTransform: 'uppercase',
    color: theme.palette.greys.medium,
  },
  panelMetaContainer: {
    marginLeft: 'auto',
  },
  panelMeta: {
    fontSize: theme.typography.pxToRem(13),
    color: theme.palette.greys.medium,
    whiteSpace: 'nowrap',
  },
  panelList: {
    flex: 1,
    minHeight: 0,
    overflow: 'auto',
    display: 'flex',
    flexDirection: 'column',
  },
  panelLoader: {
    padding: '16px',
  },
  panelHint: {
    padding: '16px',
    fontSize: theme.typography.pxToRem(13),
    color: theme.palette.greys.medium,
  },
  unreadMark: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minWidth: '22px',
    height: '22px',
    borderRadius: '11px',
    background: '#ff4d4d',
    padding: '0 6px',
    fontSize: theme.typography.pxToRem(12),
    color: '#FFF',
    lineHeight: 1,
  },
  unreadMarkContainer: {
    marginLeft: 'auto',
    display: 'flex',
  },
  unreadDot: {
    flexShrink: 0,
    alignSelf: 'center',
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    background: '#ff4d4d',
  },
  campaignItem: {
    padding: '14px 16px',
    cursor: 'pointer',
    borderLeft: '3px solid transparent',
    background: '#FFF',
    '&:hover': {
      background: theme.palette.greys.lightest,
    },
    '&.selected': {
      background: theme.palette.backgrounds.main,
      borderLeftColor: theme.palette.primary.main,
    },
    '&.disabled': {
      cursor: 'default',
      pointerEvents: 'none',
    },
  },
  campaignName: {
    fontSize: theme.typography.pxToRem(14),
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    '&.selected': {
      color: theme.palette.primary.main,
      fontWeight: 700,
    },
  },
  contactItem: {
    padding: '14px 16px',
    cursor: 'pointer',
    borderLeft: '3px solid transparent',
    background: '#FFF',
    '&:hover': {
      background: theme.palette.greys.lightest,
    },
    '&.selected': {
      background: theme.palette.backgrounds.main,
      borderLeftColor: theme.palette.primary.main,
    },
    '&.disabled': {
      cursor: 'default',
      pointerEvents: 'none',
    },
  },
  contactAvatar: {
    flexShrink: 0,
    width: '36px',
    height: '36px',
    borderRadius: '50%',
    background: theme.palette.backgrounds.main,
    color: theme.palette.primary.main,
    fontSize: theme.typography.pxToRem(13),
    fontWeight: 700,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  contactTexts: {
    flex: 1,
    minWidth: 0,
  },
  contactName: {
    fontSize: theme.typography.pxToRem(14),
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    '&.unread': {
      fontWeight: 700,
    },
  },
  contactOccupation: {
    fontSize: theme.typography.pxToRem(13),
    color: theme.palette.greys.medium,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
}));

export default CampaignList;
